import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BezpartHeader } from "../components/BezpartHeader";
import { apiFetch, type MeResponse } from "../api/client";
import { formatCabinetUserName } from "../lib/mainSiteLinks";
import { isFetchAborted, isUnauthorizedError, authEntryPathForError } from "../lib/authRedirect";

type TrashItem = { id: string; name?: string; title?: string; deleted_at: string };

export function TrashPage() {
  const navigate = useNavigate();
  const [folders, setFolders] = useState<TrashItem[]>([]);
  const [boards, setBoards] = useState<TrashItem[]>([]);
  const [userName, setUserName] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    document.body.classList.add("wb-page--cabinet", "wb-page--cabinet-miro");
    return () => {
      document.body.classList.remove("wb-page--cabinet", "wb-page--cabinet-miro");
    };
  }, []);

  async function load() {
    const data = await apiFetch<{
      folders: TrashItem[];
      boards: TrashItem[];
      build?: string;
    }>("/api/v1/trash");
    if (!Array.isArray(data.boards) || !Array.isArray(data.folders)) {
      throw new Error(
        "Корзина вернула неожиданный ответ. Обновите API (build trash-soft-delete)."
      );
    }
    setFolders(data.folders);
    setBoards(data.boards);
    if (data.build) {
      console.info("[bezpart-board] trash api build:", data.build);
    }
  }

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const me = await apiFetch<MeResponse>("/api/v1/me", { signal: ac.signal });
        if (cancelled || isFetchAborted(ac.signal)) return;
        setUserName(formatCabinetUserName(me.email || ""));
        setSessionReady(true);
        try {
          await load();
        } catch (dataErr) {
          if (cancelled || isFetchAborted(ac.signal)) return;
          setErr(dataErr instanceof Error ? dataErr.message : "Не удалось загрузить корзину");
        }
      } catch (e) {
        if (cancelled || isFetchAborted(ac.signal)) return;
        if (isUnauthorizedError(e)) {
          navigate(authEntryPathForError(e, "/trash"), { replace: true });
          return;
        }
        setErr(e instanceof Error ? e.message : "Ошибка загрузки");
        setSessionReady(true);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [navigate]);

  async function act(type: "folder" | "board", id: string, action: "restore" | "purge") {
    const path = action === "restore" ? "/api/v1/trash/restore" : "/api/v1/trash/purge";
    try {
      await apiFetch(path, { method: "POST", json: { type, id } });
      setOk(action === "restore" ? "Восстановлено" : "Удалено навсегда");
      setErr("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Ошибка");
    }
  }

  function row(type: "folder" | "board", item: TrashItem, label: string) {
    const when = item.deleted_at
      ? new Date(item.deleted_at).toLocaleString("ru-RU")
      : "";
    return (
      <article key={item.id} className="wb-board-card wb-board-card--list wb-lk-card">
        <div className="wb-board-card__row">
          <div className="wb-board-card__open-zone" style={{ cursor: "default" }}>
            <span className="wb-board-card__title">{label}</span>
            <span className="wb-board-card__meta">· удалена {when}</span>
          </div>
          <div className="wb-board-card__actions">
            <button
              type="button"
              className="wb-board-card__action-btn"
              onClick={() => void act(type, item.id, "restore")}
            >
              Восстановить
            </button>
            <button
              type="button"
              className="wb-board-card__action-btn wb-board-card__action-btn--danger"
              onClick={() => {
                if (!confirm("Удалить навсегда?")) return;
                void act(type, item.id, "purge");
              }}
            >
              Удалить навсегда
            </button>
          </div>
        </div>
      </article>
    );
  }

  if (!sessionReady) {
    return (
      <>
        <BezpartHeader mode="logged-in" userName="…" />
        <div className="wb-cabinet-wrap wb-cabinet-wrap--loading">
          <p className="wb-cabinet-loading">Загрузка…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <BezpartHeader mode="logged-in" userName={userName || "Пользователь"} />
      <div className="wb-cabinet-wrap">
        <main className="wb-cabinet-main-panel" style={{ maxWidth: 960, margin: "0 auto", width: "100%" }}>
          <header className="wb-cabinet-panel-head">
            <div className="wb-cabinet-panel-head__titles">
              <h1 className="wb-cabinet-panel-head__title">Корзина</h1>
              <p className="wb-cabinet-panel-head__sub">
                Восстановите удалённые папки и доски или удалите их окончательно.{" "}
                <Link to="/cabinet">← Мои доски</Link>
              </p>
            </div>
          </header>
          {err ? (
            <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
              {err}
            </p>
          ) : null}
          {ok ? (
            <p className="wb-cabinet-toast wb-cabinet-toast--ok" role="status">
              {ok}
            </p>
          ) : null}

          <section className="wb-cabinet-boards" style={{ marginBottom: "1.5rem" }}>
            <h2 className="wb-cabinet-sidebar__section-title" style={{ paddingLeft: 0 }}>
              Папки
            </h2>
            {folders.length === 0 ? (
              <p className="wb-cabinet-sidebar__empty">В корзине нет папок.</p>
            ) : (
              <div className="wb-board-stack">
                {folders.map((f) => row("folder", f, f.name || "Папка"))}
              </div>
            )}
          </section>
          <section className="wb-cabinet-boards">
            <h2 className="wb-cabinet-sidebar__section-title" style={{ paddingLeft: 0 }}>
              Доски
            </h2>
            {boards.length === 0 ? (
              <p className="wb-cabinet-sidebar__empty">В корзине нет досок.</p>
            ) : (
              <div className="wb-board-stack">
                {boards.map((b) => row("board", b, b.title || "Доска"))}
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}
